/**
 * Ory Kratos Message ID to Translation Key Mapping
 *
 * Maps Ory's numeric message IDs to semantic translation key identifiers.
 * The actual translations are provided by the application's i18n files.
 *
 * Source: https://www.ory.com/docs/kratos/concepts/ui-messages
 *
 * Naming Strategy:
 * - Keys describe the actual message content/purpose, not assumed implementation context
 * - Action buttons use *_ACTION suffix (SIGN_IN_ACTION, CONTINUE_ACTION)
 * - Field labels use *_FIELD suffix (PASSWORD_FIELD, EMAIL_FIELD)
 * - Info messages use *_INFO suffix (CODE_SENT_INFO, PREPARE_DEVICE_INFO)
 * - Success messages use *_SUCCESS suffix (CHANGES_SAVED_SUCCESS)
 * - Errors describe the problem in present tense (EXPIRED, INVALID, MISSING)
 * - Past-tense for completed states (ALREADY_COMPLETED, CODE_SENT)
 * - Maintain flow prefixes for flow-specific messages (LOGIN_*, REGISTRATION_*, etc.)
 */

/**
 * Flat mapping from Ory message IDs to translation key identifiers
 *
 * Note: Keys are CAPSLOCK identifiers for translation lookups.
 * When used as error codes, convert to lowercase with underscores.
 */
export const ORY_MESSAGE_ID_TO_KEY: Record<number, string> = {
  // ==========================================
  // Login Flow - Info/Actions (1010xxx)
  // ==========================================

  1010001: 'SIGN_IN_ACTION',                    // "Sign in"
  1010002: 'SIGN_IN_WITH_PROVIDER_ACTION',      // "Sign in with {provider}"
  1010003: 'CONFIRM_ACTION_INFO',               // "Please confirm this action by verifying that it is you."
  1010004: 'COMPLETE_2FA_INFO',                 // "Please complete the second authentication challenge."
  1010005: 'VERIFY_ACTION',                     // "Verify"
  1010006: 'AUTHENTICATION_CODE_FIELD',         // "Authentication code"
  1010007: 'BACKUP_RECOVERY_CODE_FIELD',        // "Backup recovery code"
  1010008: 'SIGN_IN_HARDWARE_KEY_ACTION',       // "Sign in with hardware key"
  1010009: 'USE_AUTHENTICATOR_ACTION',          // "Use Authenticator"
  1010010: 'USE_BACKUP_CODE_ACTION',            // "Use backup recovery code"
  1010011: 'SIGN_IN_HARDWARE_KEY_DUPLICATE',    // "Sign in with hardware key" (duplicate)
  1010012: 'PREPARE_WEBAUTHN_DEVICE_INFO',      // "Prepare your WebAuthn device..."
  1010013: 'CONTINUE_ACTION',                   // "Continue"
  1010014: 'CODE_SENT_CHECK_SPELLING_INFO',     // "A code was sent to the address you provided..."
  1010015: 'SEND_SIGN_IN_CODE_ACTION',          // "Send sign in code"
  1010016: 'DUPLICATE_IDENTIFIER_LINK_INFO',    // "You tried to sign in with... add your account as another way to sign in"
  1010017: 'SIGN_IN_AND_LINK_ACTION',           // "Sign in and link"
  1010018: 'CONFIRM_WITH_PROVIDER_ACTION',      // "Confirm with {provider}"
  1010019: 'REQUEST_CODE_TO_CONTINUE_ACTION',   // "Request code to continue"
  1010021: 'SIGN_IN_WITH_PASSKEY_ACTION',       // "Sign in with passkey"
  1010022: 'SIGN_IN_WITH_PASSWORD_ACTION',      // "Sign in with password"
  1010023: 'SEND_CODE_TO_ADDRESS_ACTION',       // "Send code to {address}"

  // ==========================================
  // Registration Flow - Info/Actions (1040xxx)
  // ==========================================

  1040001: 'SIGN_UP_ACTION',                    // "Sign up"
  1040002: 'SIGN_UP_WITH_PROVIDER_ACTION',      // "Sign up with {provider}"
  1040003: 'REGISTRATION_CONTINUE_ACTION',      // "Continue"
  1040004: 'SIGN_UP_WITH_SECURITY_KEY_ACTION',  // "Sign up with security key"
  1040005: 'REGISTRATION_CODE_SENT_INFO',       // "A code has been sent to the address(es) you provided..."
  1040006: 'SEND_SIGN_UP_CODE_ACTION',          // "Send sign up code"
  1040007: 'SIGN_UP_WITH_PASSKEY_ACTION',       // "Sign up with passkey"
  1040008: 'BACK_ACTION',                       // "Back"
  1040009: 'CHOOSE_CREDENTIAL_INFO',            // "Please choose a credential to authenticate yourself with."

  // ==========================================
  // Settings Flow - Info/Actions (1050xxx)
  // ==========================================

  1050001: 'CHANGES_SAVED_SUCCESS',             // "Your changes have been saved!"
  1050002: 'LINK_PROVIDER_ACTION',              // "Link {provider}"
  1050003: 'UNLINK_PROVIDER_ACTION',            // "Unlink {provider}"
  1050004: 'UNLINK_TOTP_ACTION',                // "Unlink TOTP Authenticator App"
  1050005: 'AUTHENTICATOR_QR_CODE_LABEL',       // "Authenticator app QR code"
  1050006: 'SECRET_TEXT',                       // "{secret}"
  1050007: 'REVEAL_BACKUP_CODES_ACTION',        // "Reveal backup recovery codes"
  1050008: 'GENERATE_BACKUP_CODES_ACTION',      // "Generate new backup recovery codes"
  1050009: 'SECRET_TEXT_DUPLICATE',             // "{secret}" (duplicate)
  1050010: 'BACKUP_CODES_KEEP_SAFE_INFO',       // "These are your back up recovery codes. Please keep them in a safe place!"
  1050011: 'CONFIRM_BACKUP_CODES_ACTION',       // "Confirm backup recovery codes"
  1050012: 'ADD_SECURITY_KEY_ACTION',           // "Add security key"
  1050013: 'SECURITY_KEY_NAME_FIELD',           // "Name of the security key"
  1050014: 'SECRET_USED_AT_INFO',               // "Secret was used at {timestamp}"
  1050015: 'SECRETS_LIST',                      // "{secrets_list}" (complex with nested messages)
  1050016: 'DISABLE_METHOD_ACTION',             // "Disable this method"
  1050017: 'AUTHENTICATOR_SECRET_INFO',         // "This is your authenticator app secret..."
  1050018: 'REMOVE_SECURITY_KEY_ACTION',        // "Remove security key \"{display_name}\""
  1050019: 'ADD_PASSKEY_ACTION',                // "Add passkey"
  1050020: 'REMOVE_PASSKEY_ACTION',             // "Remove passkey \"{display_name}\""

  // ==========================================
  // Recovery Flow - Success/Info (1060xxx)
  // ==========================================

  1060001: 'RECOVERY_SUCCESS_CHANGE_PASSWORD',  // "You successfully recovered your account. Please change your password..."
  1060002: 'RECOVERY_LINK_EMAIL_SENT_INFO',     // "An email containing a recovery link has been sent..."
  1060003: 'RECOVERY_CODE_EMAIL_SENT_INFO',     // "An email containing a recovery code has been sent..."
  1060004: 'RECOVERY_CODE_SENT_TO_MASKED_INFO', // "A recovery code has been sent to {masked_address}..."
  1060005: 'PROVIDE_RECOVERY_ADDRESS_INFO',     // "Recover access to your account by providing your recovery address in full."
  1060006: 'RECOVERY_METHOD_CHOICE_INFO',       // "How do you want to recover your account?"
  1060007: 'RECOVERY_BACK_ACTION',              // "Back"

  // ==========================================
  // Generic Fields/Actions (1070xxx)
  // ==========================================

  1070001: 'PASSWORD_FIELD',                    // "Password"
  1070002: 'DYNAMIC_FIELD',                     // "{title}" (dynamic field)
  1070003: 'SAVE_ACTION',                       // "Save"
  1070004: 'ID_FIELD',                          // "ID"
  1070005: 'SUBMIT_ACTION',                     // "Submit"
  1070006: 'VERIFY_CODE_ACTION',                // "Verify code"
  1070007: 'EMAIL_FIELD',                       // "Email"
  1070008: 'RESEND_CODE_ACTION',                // "Resend code"
  1070009: 'GENERIC_CONTINUE_ACTION',           // "Continue"
  1070010: 'RECOVERY_CODE_FIELD',               // "Recovery code"
  1070011: 'VERIFICATION_CODE_FIELD',           // "Verification code"
  1070012: 'REGISTRATION_CODE_FIELD',           // "Registration code"
  1070013: 'LOGIN_CODE_FIELD',                  // "Login code"
  1070014: 'LOGIN_AND_LINK_CREDENTIAL_ACTION',  // "Login and link credential"
  1070015: 'COMPLETE_CAPTCHA_INFO',             // "Please complete the captcha challenge to continue."
  1070016: 'RECOVERY_ADDRESS_FIELD',            // "Recovery address"
  1070017: 'PHONE_NUMBER_FIELD',                // "Phone number"

  // ==========================================
  // Verification Flow - Info/Success (1080xxx)
  // ==========================================

  1080001: 'VERIFICATION_LINK_EMAIL_SENT_INFO', // "An email containing a verification link has been sent..."
  1080002: 'EMAIL_VERIFIED_SUCCESS',            // "You successfully verified your email address."
  1080003: 'VERIFICATION_CODE_EMAIL_SENT_INFO', // "An email containing a verification code has been sent..."

  // ==========================================
  // Validation Errors - Generic (4000xxx)
  // ==========================================

  4000001: 'GENERIC_ERROR_WITH_REASON',         // "{reason}"
  4000002: 'PROPERTY_MISSING',                  // "Property {property} is missing."
  4000003: 'LENGTH_TOO_SHORT_WITH_VALUES',      // "length must be >= 5, but got 3"
  4000004: 'PATTERN_MISMATCH',                  // "does not match pattern \"{pattern}\""
  4000005: 'PASSWORD_UNUSABLE_REASON',          // "The password can not be used because {reason}."
  4000006: 'CREDENTIALS_INVALID',               // "The provided credentials are invalid..."
  4000007: 'IDENTIFIER_ALREADY_EXISTS',         // "An account with the same identifier... exists already."
  4000008: 'AUTHENTICATION_CODE_INVALID',       // "The provided authentication code is invalid, please try again."
  4000009: 'NO_LOGIN_IDENTIFIERS_FOUND',        // "Could not find any login identifiers..."
  4000010: 'ACCOUNT_NOT_ACTIVE_VERIFY_EMAIL',   // "Account not active yet. Did you forget to verify your email address?"
  4000011: 'NO_TOTP_DEVICE',                    // "You have no TOTP device set up."
  4000012: 'BACKUP_CODE_ALREADY_USED',          // "This backup recovery code has already been used."
  4000013: 'NO_WEBAUTHN_DEVICE',                // "You have no WebAuthn device set up."
  4000014: 'NO_BACKUP_CODES',                   // "You have no backup recovery codes set up."
  4000015: 'ACCOUNT_NO_SECURITY_KEY',           // "This account does not exist or has no security key set up."
  4000016: 'BACKUP_CODE_INVALID',               // "The backup recovery code is not valid."
  4000017: 'LENGTH_TOO_LONG_WITH_VALUES',       // "length must be <= 5, but got 6"
  4000018: 'NUMBER_TOO_SMALL_GREATER_EQUAL',    // "must be >= 5 but found 3"
  4000019: 'NUMBER_TOO_SMALL_GREATER',          // "must be > 5 but found 5"
  4000020: 'NUMBER_TOO_LARGE_LESS_EQUAL',       // "must be <= 5 but found 6"
  4000021: 'NUMBER_TOO_LARGE_LESS',             // "must be < 5 but found 5"
  4000022: 'NOT_MULTIPLE_OF',                   // "3 not multipleOf 7"
  4000023: 'TOO_MANY_ITEMS',                    // "maximum 3 items allowed, but found 4 items"
  4000024: 'TOO_FEW_ITEMS',                     // "minimum 3 items allowed, but found 2 items"
  4000025: 'DUPLICATE_ITEMS',                   // "items at index 0 and 2 are equal"
  4000026: 'TYPE_MISMATCH',                     // "expected {allowed_types_list}, but got {actual_type}"
  4000027: 'IDENTIFIER_EXISTS_LINK_SOCIAL',     // "An account... exists already. Please sign in to... link your social profile."
  4000028: 'CREDENTIAL_IN_USE_BY_ANOTHER',      // "You tried signing in with... already in use by another account..."
  4000029: 'CONSTANT_MISMATCH',                 // "must be equal to constant {expected}"
  4000030: 'CONST_FAILED',                      // "const failed"
  4000031: 'PASSWORD_TOO_SIMILAR_TO_IDENTIFIER',// "The password can not be used because it is too similar to the identifier."
  4000032: 'PASSWORD_TOO_SHORT',                // "The password must be at least 6 characters long, but got 5."
  4000033: 'PASSWORD_TOO_LONG',                 // "The password must be at most 72 characters long, but got 80."
  4000034: 'PASSWORD_FOUND_IN_BREACH',          // "The password has been found in data breaches and must no longer be used."
  4000035: 'ACCOUNT_NO_CODE_SIGNIN',            // "This account does not exist or has not setup sign in with code."
  4000036: 'TRAITS_MISMATCH',                   // "The provided traits do not match the traits previously associated with this flow."
  4000037: 'ACCOUNT_NO_LOGIN_METHOD',           // "This account does not exist or has no login method configured."
  4000038: 'CAPTCHA_FAILED',                    // "Captcha verification failed, please try again."
  4000039: 'NEW_PASSWORD_SAME_AS_OLD',          // "The new password must be different from the old password."

  // ==========================================
  // Login Flow Errors (4010xxx)
  // ==========================================

  4010001: 'LOGIN_FLOW_EXPIRED',                // "The login flow expired... please try again."
  4010002: 'LOGIN_NO_STRATEGY_FOUND',           // "Could not find a strategy to log you in with..."
  4010003: 'REGISTRATION_NO_STRATEGY_FOUND',    // "Could not find a strategy to sign you up with..."
  4010004: 'SETTINGS_NO_STRATEGY_FOUND',        // "Could not find a strategy to update your settings..."
  4010005: 'RECOVERY_NO_STRATEGY_FOUND',        // "Could not find a strategy to recover your account with..."
  4010006: 'VERIFICATION_NO_STRATEGY_FOUND',    // "Could not find a strategy to verify your account with..."
  4010007: 'REQUEST_ALREADY_COMPLETED',         // "The request was already completed successfully and can not be retried."
  4010008: 'LOGIN_CODE_INVALID',                // "The login code is invalid or has already been used..."
  4010009: 'LINKED_CREDENTIALS_MISMATCH',       // "Linked credentials do not match."
  4010010: 'ADDRESS_MISMATCH',                  // "The address you entered does not match any known addresses in the current account."

  // ==========================================
  // Registration Flow Errors (4040xxx)
  // ==========================================

  4040001: 'REGISTRATION_FLOW_EXPIRED',         // "The registration flow expired... please try again."
  4040002: 'REGISTRATION_ALREADY_COMPLETED',    // "The request was already completed successfully and can not be retried."
  4040003: 'REGISTRATION_CODE_INVALID',         // "The registration code is invalid or has already been used..."

  // ==========================================
  // Settings Flow Errors (4050xxx)
  // ==========================================

  4050001: 'SETTINGS_FLOW_EXPIRED',             // "The settings flow expired... please try again."

  // ==========================================
  // Recovery Flow Errors (4060xxx)
  // ==========================================

  4060001: 'RECOVERY_ALREADY_COMPLETED',        // "The request was already completed successfully and can not be retried."
  4060002: 'RECOVERY_FLOW_FAILED',              // "The recovery flow reached a failure state and must be retried."
  4060004: 'RECOVERY_TOKEN_INVALID',            // "The recovery token is invalid or has already been used..."
  4060005: 'RECOVERY_FLOW_EXPIRED',             // "The recovery flow expired... please try again."
  4060006: 'RECOVERY_CODE_INVALID',             // "The recovery code is invalid or has already been used..."

  // ==========================================
  // Verification Flow Errors (4070xxx)
  // ==========================================

  4070001: 'VERIFICATION_TOKEN_INVALID',        // "The verification token is invalid or has already been used..."
  4070002: 'VERIFICATION_ALREADY_COMPLETED',    // "The request was already completed successfully and can not be retried."
  4070003: 'VERIFICATION_FLOW_FAILED',          // "The verification flow reached a failure state and must be retried."
  4070005: 'VERIFICATION_FLOW_EXPIRED',         // "The verification flow expired... please try again."
  4070006: 'VERIFICATION_CODE_INVALID',         // "The verification code is invalid or has already been used..."

  // ==========================================
  // Server Errors (5000xxx)
  // ==========================================

  5000001: 'SYSTEM_ERROR',                      // "{reason}"
} as const;

/**
 * Type-safe Ory message keys (CAPSLOCK identifiers)
 *
 * Example: "CREDENTIALS_INVALID", "PASSWORD_TOO_SHORT"
 */
export type OryMessageKey = typeof ORY_MESSAGE_ID_TO_KEY[keyof typeof ORY_MESSAGE_ID_TO_KEY];

/**
 * Type-safe Ory error codes (lowercase_with_underscores)
 *
 * These are derived from OryMessageKey by converting to lowercase.
 * Example: "CREDENTIALS_INVALID" → "credentials_invalid"
 */
export type OrySemanticErrorCode = Lowercase<OryMessageKey>;
